<template>
  <div data-testid="add-access-tab" class="d-flex flex-column gap-4 mt-4">
    <CommonFilter 
        data-testid="add-access-tab__filter"
        :filterheaders="filterheaders"
        :loading="loading"
        :searchOption="true"
        :cleanSelectAll="false"
        @loadFilterData="handleFilterData"
        @resetFilters="handleFilterData"
    />
    <div class="d-flex flex-column gap-4">
        <!-- Loading State: Shimmer Table -->
        <div v-if="loading" class="d-flex flex-column gap-4">
            <lb-datatablenew
                key="shimmer-table"
                :headers="headers"
                :items="[{id:1},{id:2},{id:3},{id:4},{id:5}]"
                :enableslot="headers.map(h => h.value)"
                class="elevation-0"
                :hide-default-footer="true"
                :loading="false"
            >
                <template v-for="header in headers" v-slot:[header.value]>
                    <v-skeleton-loader :key="header.value" type="text" class="my-2"></v-skeleton-loader>
                </template>
            </lb-datatablenew>
        </div>

        <div v-else class="d-flex flex-column gap-4">
            <div v-if="selectedData.length > 0" class="d-flex align-center justify-end gap-4 add-access-tab__top-bar ml-auto">
                <h5 class="font-weight-medium primary--text">{{ selectedData.length }} Selected</h5>
                <base-button primary :disabled="!isPermissionEnabled" @click="promptAdd(selectedData)" data-testid="add-access-tab__top-bulk-add-btn" label="Add Access" />
            </div>

            <lb-datatablenew
                key="data-table"
                data-testid="add-access-tab__datatable"
                :headers="headers"
                :items="displayedItems"
                :loading="false"
                :enableslot="['indexno', 'code', 'action']"
                :multiselectkey="isPermissionEnabled ? '_id' : ''"
                :selecteditems="selectedData.map(x => x._id)"
                @selectItem="handleSelection"
                class="elevation-0"
                :hide-default-footer="true"
            >
                <template v-slot:indexno="{ item }">
                    <div class="text-center">
                        {{ (page - 1) * itemsPerPage + displayedItems.indexOf(item) + 1 }}
                    </div>
                </template>
                <template v-slot:code="{ item }">
                    <div data-testid="add-access-tab__code-name">
                        <span class="">{{ item.code }}</span> - <span>{{ item.name }}</span>
                    </div>
                </template>
                <template v-slot:action="{ item }">
                    <div class="d-flex justify-center">
                        <v-tooltip bottom content-class="tooltip-bottom">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on">
                                    <base-button icon="mdi-plus-circle-outline" color="primary" :disabled="!isPermissionEnabled" @click="promptAdd([item])" data-testid="add-access-tab__add-btn" />
                                </span>
                            </template>
                            <span>{{ isPermissionEnabled ? 'Add Access' : 'Feature Disabled' }}</span>
                        </v-tooltip>
                    </div>
                </template>
            </lb-datatablenew>

            <Pagination 
                v-if="(items.length > 0) || (serverSide && totalRecords > 0)" 
                :list="items"
                :itemsperpage="itemsPerPage"
                :value="page"
                :lengthOverride="totalPages"
                @input="handlePageInput"
                @paginated-data="handlePaginatedData"
                class="mt-4" 
            />
        </div>
    </div>
    <UserPermissionConfirmationDialog
        v-if="confirmDialog"
        v-model="confirmDialog"
        title="Add Access"
        :items="itemsToAdd"
        message="Add user access for selected reporting units"
        type="add"
        @confirm="confirmAdd"
    />
  </div>
</template>

<script>
import CommonFilter from '@/views/common/Filter.vue';
import UserPermissionConfirmationDialog from './UserPermissionConfirmationDialog.vue';
import Pagination from '../../../../../views/common/Pagination.vue';

export default {
    name: 'AddAccessTab',
    components: {
        CommonFilter,
        UserPermissionConfirmationDialog,
        Pagination
    },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        allItems: {
            type: Array,
            default: () => []
        },
        filterOptions: {
            type: Object,
            default: () => ({})
        },
        loading: {
            type: Boolean,
            default: false
        },
        totalRecords: {
            type: Number,
            default: 0
        },
        serverSide: {
            type: Boolean,
            default: false
        },
        currentPage: {
            type: Number,
            default: 1
        },
        isPermissionEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedData: [],
            hasFiltered: false,
            confirmDialog: false,
            itemsToAdd: [],
            paginatedItems: [],
            page: 1,
            itemsPerPage: 100
        }
    },
    watch: {
        currentPage: {
            handler(val) {
                if (val && val !== this.page) {
                    this.page = val;
                }
            },
            immediate: true
        }
    },
    computed: {
        displayedItems() {
             return this.serverSide ? this.items : this.paginatedItems;
        },
        totalPages() {
            if (this.serverSide && this.totalRecords > 0) {
                return Math.ceil(this.totalRecords / this.itemsPerPage);  
            }
            return 0;
        },
        headers() {
            return [
                {
                    text: 'S.No',
                    value: 'indexno',
                    sortable: false,
                    width: '60px',
                    align: 'center'
                },
                {
                    text: 'RU Code & Description',
                    value: 'code',
                    sortable: false,
                    filterable: true
                },
                {
                    text: 'Network',
                    value: 'network',
                    sortable: false,
                    filterable: true
                },
                {
                    text: 'Region',
                    value: 'region',
                    sortable: false,
                    filterable: true
                },
                {
                    text: 'Country',
                    value: 'country',
                    sortable: false,
                    filterable: true
                },
                {
                    text: 'Actions',
                    value: 'action',
                    sortable: false,
                    width: '100px'
                }
            ];
        },
        filterheaders() {
            const hasExternalFilters = this.filterOptions && Object.keys(this.filterOptions).length > 0;
            // Use external filters (complete list) if available, otherwise just use what items we have (local)
            // But if serverSide is true, 'items' is just one page, so we really want external filters.

            const normalizeNameOptions = (list) => {
                if (!Array.isArray(list)) return [];
                return list
                    .map((item) => {
                        if (item == null) return null;
                        if (typeof item === 'string' || typeof item === 'number') {
                            const name = String(item).trim();
                            return name ? { name } : null;
                        }
                        if (typeof item === 'object') {
                            const name = String(item.name ?? item.value ?? item.label ?? '').trim();
                            return name ? { ...item, name } : null;
                        }
                        return null;
                    })
                    .filter(Boolean);
            };
            
            // Helper to get options for a key
            const getOptions = (key) => {
                if (hasExternalFilters && this.filterOptions[key] && this.filterOptions[key].length > 0) {
                    // CommonFilter expects options to be objects (e.g., { name: 'APAC' }).
                    // Some callers may pass plain strings, so normalize for safety.
                    return normalizeNameOptions(this.filterOptions[key]);
                }
                // Fallback to local data unique values
                const source = (this.allItems && this.allItems.length > 0) ? this.allItems : (this.items || []);
                return [...new Set(source.map(i => i[key]).filter(x => x && x !== 'NA'))]
                    .map(x => String(x).trim())
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b))
                    .map(x => ({ name: x }));
            };

            // For reporting units, if we have a lot of data (server side), we probably shouldn't try to list them all in a dropdown 
            // from the partial data unless we have a specific search API or a full list provided.
            // Since we don't have a "full list of unassigned RU names" readily passed as a simple list (unlike region/network),
            // we will stick to what we have in 'allItems' or 'items', OR if we want to enable searching by RU name, 
            // the backend filter likely supports it, but the dropdown visual needs data.
            // For now, let's just use what we have in allItems (which might be 100).
            const source = (this.allItems && this.allItems.length > 0) ? this.allItems : (this.items || []);
            const getUniqueRU = () => source
                .map(i => {
                    const name = String(i?.name || i?.code || '').trim();
                    return (i && i._id && name) ? { _id: i._id, name } : null;
                })
                .filter(Boolean)
                .filter((v, i, a) => a.findIndex(t => (t._id === v._id)) === i);

            // Using 'name' as value key for text fields, '_id' for RU
            return [
                { key: 'network', name: 'Network', type: 'multiselect', value: 'name', data: getOptions('network') },
                { key: 'region', name: 'Region', type: 'multiselect', value: 'name', data: getOptions('region') },
                { key: 'country', name: 'Country', type: 'multiselect', value: 'name', data: getOptions('country') },
                { key: 'type', name: 'RU Type', type: 'multiselect', value: 'name', data: getOptions('type') },
                { key: 'reportingunit', name: 'Reporting Unit', type: 'multiselect', value: 'name', data: getUniqueRU() }
            ];
        }
    },
    methods: {
        handleSelection(ids) {
            this.selectedData = this.items.filter(item => ids.indexOf(item._id) !== -1);
        },
        handleFilterData(event) {
            const filter = (event && event.filter) ? event.filter : (event || {});
            
            // Check if any filter has a value
            let isActive = false;
            // With cleanSelectAll=false in CommonFilter, "Select All" now sends actual values (not []).
            // "Select None" sends [].
            
            if (filter && typeof filter === 'object') {
                const keys = Object.keys(filter);
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    const value = filter[key];
                    // Check for non-empty arrays (multiselect)
                    if (Array.isArray(value) && value.length > 0) {
                        isActive = true;
                        break;
                    }
                    // Check for non-empty primitives (singleselect, search)
                    else if (value !== null && value !== undefined && value !== '' && !Array.isArray(value) && typeof value !== 'object') {
                        isActive = true;
                        break;
                    }
                }
            }

            this.hasFiltered = isActive;
            // Emit filter data to parent components
            this.$emit('filter-data', filter);
        
        },
        promptAdd(items) {
            if (!this.isPermissionEnabled) return;
            this.itemsToAdd = items;
            this.confirmDialog = true;
        },
        handlePageInput(val) {
            this.page = val;
            if (this.serverSide) {
                this.$emit('page-change', val);
            }
        },
        handlePaginatedData(data) {
            if (!this.serverSide) {
                this.paginatedItems = data.data || [];
            }
        },
        confirmAdd() {
            this.$emit('add', this.itemsToAdd);
            this.confirmDialog = false;
            this.selectedData = [];
            this.itemsToAdd = [];
        }
    }
}
</script>

<style scoped>
.role-select {
    width: 180px;
}
.add-access-tab__top-bar {
    border: 1px solid var(--color-primary-base);
    padding: var(--space-xs) var(--space-xs);
    border-radius: var(--radius-sm);
    width: fit-content;
}
</style>